import { useCallback, useEffect, useMemo, useState } from 'react';

import { browserName } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';

import { EUISnackBarMessageType } from '@aquariuxfintech/aqx-trader-react-ui/snack-bar';

import { ELoginAs, ELoginSource } from '@aqx-core/common/api/auth';
import { APP_ROUTES } from '@aqx-core/common/constants/router';
import { i18n } from '@aqx-core/common/i18n';
import { useQueryParams } from '@aqx-core/common/router/hooks/web/use-query-string-parms';
import { authenticationViewController } from '@aqx-core/common/services/auth/controller';
import { useCompanyAccess, useCompanyProfile } from '@aqx-core/common/services/company';

export interface IUseExperienceTokenParams {
  source: ELoginSource.MOBILE_WEB | ELoginSource.WEB;
  onShowNotification: (message: string, messageType: EUISnackBarMessageType) => void;
  onSuccess: () => void;
}

export const useExperienceToken = (params: IUseExperienceTokenParams) => {
  const { onShowNotification, onSuccess, source } = params;

  const { hasExperienceAccess, hasCentroidDemoLoginConfig, hasMT5DemoLoginConfig } = useCompanyAccess();
  const company = useCompanyProfile();
  const { token, extToken } = useQueryParams();
  const navigate = useNavigate();

  const loginAs = useMemo(() => {
    if (hasCentroidDemoLoginConfig) {
      return ELoginAs.CENTROID_DEMO;
    }
    return hasMT5DemoLoginConfig ? ELoginAs.MT5_DEMO : ELoginAs.MT4_DEMO;
  }, [hasCentroidDemoLoginConfig, hasMT5DemoLoginConfig]);

  const [isApiCalled, setIsApiCalled] = useState(false); // Ensure API is only called once

  const onError = useCallback(
    (message: string) => {
      onShowNotification(message, EUISnackBarMessageType.ERROR);
    },
    [onShowNotification]
  );

  useEffect(() => {
    const handleLoginToken = async () => {
      if (token && company && !isApiCalled) {
        setIsApiCalled(true);
        await authenticationViewController.loginWithExperienceToken(
          { browserType: browserName, source, deviceType: undefined, token: token },
          loginAs,
          onSuccess,
          onError
        );
      }
    };
    handleLoginToken();
  }, [token, onSuccess, onShowNotification, source, loginAs, onError, company, isApiCalled]);

  useEffect(() => {
    const handleExtToken = async () => {
      if (extToken && company && !isApiCalled) {
        setIsApiCalled(true);

        const onExtendSuccess = () => {
          onShowNotification(i18n.t('Your demo account has been extended by 7 days.'), EUISnackBarMessageType.SUCCESS);
        };

        await authenticationViewController.loginWithExperienceExtendToken(
          { browserType: browserName, source, deviceType: undefined, token: extToken },
          loginAs,
          onExtendSuccess,
          onSuccess,
          onError
        );
      }
    };
    handleExtToken();
  }, [extToken, onSuccess, onShowNotification, source, loginAs, onError, company, isApiCalled]);

  useEffect(() => {
    if (token) {
      if (hasExperienceAccess) {
        authenticationViewController.setLoginAs(loginAs);
      }
      authenticationViewController.initWithToken(token, () => {
        navigate(APP_ROUTES.HOME);
      });
    }
  }, [token, hasExperienceAccess, hasMT5DemoLoginConfig, navigate, loginAs]);
};
